import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogHeader as SheetHeader,
  DialogTitle as SheetTitle,
  DialogDescription as SheetDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Plus,
  Trash2,
  Pencil,
  Check,
  ChevronLeft,
  Sparkles,
  Send,
  MapPin,
  Clock,
  Star,
  UserPlus,
  Users,
  CalendarPlus,
  Info,
  User,
  Coins,
  Mail,
  Calendar,
  CreditCard,
  ExternalLink,
  FileText,
  Link2,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/hooks/use-workspace";
import { LateFeeSettingsDialog } from "@/components/late-fee-settings-dialog";
import type { UserProfile, Talent, EventOffering, ClientTypeRecord, Region, ConfirmationEmailTemplate } from "@shared/schema";

// ── Fallback used only when the workspace client-types API returns empty
// (e.g. brand new workspace with no seed data). These are workspace-level
// booking-client categories — who books the talent — NOT entertainment
// industry/talent categories (those live in platform_business_types).
const DEFAULT_CLIENT_TYPES = [
  "Corporate", "Student Organization", "Theater/Venue", "Private", "Other",
];

const DEFAULT_FALLBACK_TEMPLATE_SUBJECT = "Booking Confirmation — {{account_name}}";
const DEFAULT_FALLBACK_TEMPLATE_BODY = `<p>Hi {{contact_name}},</p>

<p>Your booking has been confirmed. Here are the details:</p>

<ul>
  <li><strong>Date:</strong> {{event_date}}</li>
  <li><strong>Time:</strong> {{event_time}}</li>
  <li><strong>Location:</strong> {{location}}</li>
  <li><strong>Talent:</strong> {{talent}}</li>
</ul>

<p>Please review your invoice and complete payment:</p>
<p><a href="{{invoice_url}}">View Invoice</a></p>

<p>We look forward to working with you. If you have any questions, just reply to this email.</p>

<p>Best regards,<br>{{sender_name}}</p>`;

// ── OnboardingAIPanel ──────────────────────────────────────────────────
type OnboardingStepKey = "workspace_identity" | "talent" | "offerings" | "client_types" | "regions";

interface OnboardingAIProps {
  stepKey: OnboardingStepKey;
  creditsUsed: number;
  creditCap: number;
  creditsPerMessage: number;
  sessionId: string;
  onCreditsConsumed: (newTotal: number) => void;
  onCreditWarning: () => void;
}

function CreditIndicator({ creditsUsed, creditCap }: { creditsUsed: number; creditCap: number }) {
  const remaining = Math.max(0, creditCap - creditsUsed);
  const exhausted = remaining <= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 normal-case tracking-normal ${
        exhausted ? "text-destructive" : "text-muted-foreground"
      }`}
      data-testid="text-wizard-credits"
      title="Free AI credits shared across all onboarding steps"
    >
      <Coins className="w-3.5 h-3.5" />
      {exhausted ? "0 credits left" : `${remaining} / ${creditCap} credits`}
    </span>
  );
}

const STEP_PLACEHOLDERS: Record<OnboardingStepKey, string> = {
  workspace_identity: "e.g. How do I name a booking agency?",
  talent:        "e.g. What should I put in my bio?",
  offerings:     "e.g. How should I price a 60-minute show?",
  client_types:  "e.g. Should I separate corporate from college events?",
  regions:       "e.g. How granular should my regions be?",
};

const STEP_OPENING: Record<OnboardingStepKey, string> = {
  workspace_identity: "I can help you name your entertainment booking company — tell me about your business and I'll suggest naming approaches that work well in this industry.",
  talent:       "I can help you set up your talent profiles. Tell me about your act(s) and I'll guide you through what to include for maximum bookability.",
  offerings:    "I can help you structure your packages and pricing. What types of performances do you offer, and how long do your typical shows run?",
  client_types: "I can help you categorize who books you. Tell me about your typical clients and I'll suggest the right categories for your pipeline.",
  regions:      "I can help you decide how to structure your booking regions. Tell me about where you perform and I'll suggest a setup that makes sense for your business.",
};

function OnboardingAIPanel({ stepKey, creditsUsed, creditCap, creditsPerMessage, sessionId, onCreditsConsumed, onCreditWarning }: OnboardingAIProps) {
  const { buildPath } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Array<{ role: "user" | "assistant"; content: string }>>([
    { role: "assistant", content: STEP_OPENING[stepKey] },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [usingWorkspaceCredits, setUsingWorkspaceCredits] = useState(false);
  const [workspaceCreditExhausted, setWorkspaceCreditExhausted] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const creditsRemaining = Math.max(0, creditCap - creditsUsed);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending || workspaceCreditExhausted) return;
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setSending(true);
    try {
      const res = await apiRequest("POST", buildPath("/api/voice-assistant/onboarding-chat"), {
        stepKey,
        userText: trimmed,
        history: messages.slice(-10),
        sessionId,
      });
      const json = (await res.json()) as {
        reply?: string;
        creditsUsed?: number;
        creditsRemaining?: number;
        usingWorkspaceCredits?: boolean;
        giftJustExhausted?: boolean;
        exhausted?: boolean;
        workspaceCreditExhausted?: boolean;
        message?: string;
      };
      if (res.status === 402) {
        if (json.workspaceCreditExhausted) {
          setWorkspaceCreditExhausted(true);
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: "Your workspace AI credit balance is too low to continue. Add credits in Settings → AI Credits to keep going." },
          ]);
        } else {
          onCreditWarning();
          onCreditsConsumed(creditCap);
        }
        setSending(false);
        return;
      }
      if (json.reply) {
        setMessages((prev) => [...prev, { role: "assistant", content: json.reply! }]);
      }
      if (typeof json.creditsUsed === "number") {
        onCreditsConsumed(json.creditsUsed);
      }
      if (json.giftJustExhausted) {
        onCreditWarning();
      }
      if (json.usingWorkspaceCredits) {
        setUsingWorkspaceCredits(true);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Something went wrong. Try again in a moment." },
      ]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        data-testid={`button-toggle-ai-${stepKey}`}
      >
        <Sparkles className="w-3.5 h-3.5" />
        {open ? "Hide Clarik AI" : "Ask Clarik AI"}
        {creditsUsed > 0 && (
          <span className="text-xs text-muted-foreground font-normal">
            ({creditsRemaining} credits left)
          </span>
        )}
      </button>

      {open && (
        <div className="rounded-lg border border-border bg-card flex flex-col" style={{ height: 300 }}>
          <div className="flex items-center gap-2 border-b px-3 py-2 shrink-0">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-semibold">Clarik AI</span>
            {usingWorkspaceCredits ? (
              <span className="ml-auto text-[10px] text-amber-600 dark:text-amber-400">
                Using workspace credits
              </span>
            ) : creditsUsed > 0 ? (
              <span className="ml-auto text-[10px] text-muted-foreground">
                {creditsRemaining} / {creditCap} credits
              </span>
            ) : null}
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-3 space-y-3"
            data-testid={`list-chat-messages-${stepKey}`}
          >
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-foreground"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg px-3 py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
          </div>

          <div className="border-t p-2 shrink-0 flex gap-2">
            <Input
              ref={inputRef}
              className="h-8 text-sm"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder={workspaceCreditExhausted ? "AI credits exhausted" : STEP_PLACEHOLDERS[stepKey]}
              disabled={sending || workspaceCreditExhausted}
              data-testid={`input-onboarding-chat-${stepKey}`}
            />
            <Button
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={send}
              disabled={sending || !input.trim() || workspaceCreditExhausted}
              data-testid={`button-chat-send-${stepKey}`}
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface OnboardingWizardProps {
  profile: UserProfile;
  canManageTalent: boolean;
  canManageClientTypes: boolean;
  canManageRegions: boolean;
  canManageEventOfferings: boolean;
  canManageBrand: boolean;
}

type DrawerStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

// ── Shared centered-modal wrapper — non-dismissible, wide ─────────────
function OnboardingSheet({
  open,
  onDismiss,
  children,
}: {
  open: boolean;
  onDismiss?: () => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && onDismiss) onDismiss();
      }}
    >
      <DialogContent
        className="max-w-2xl w-full p-0 gap-0 flex flex-col h-[calc(100dvh-1rem)] max-h-[90dvh] min-h-0"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}

// ── Drawer 0: Terms of Service ─────────────────────────────────────────
function TermsDrawer({
  open,
  stepLabel,
  onSubmit,
  onDismiss,
}: {
  open: boolean;
  stepLabel: string;
  onSubmit: () => void;
  onDismiss: () => void;
}) {
  const { toast } = useToast();
  const { buildPath } = useWorkspace();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!accepted) {
      toast({ title: "Please accept the Terms of Service to continue", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest("PATCH", "/api/profile/tos", {});
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/profile-real"] });
      onSubmit();
    } catch (err: any) {
      toast({ title: "Error", description: err?.message || "Could not record acceptance.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingSheet open={open} onDismiss={onDismiss}>
      <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
        <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
          <span>{stepLabel}</span>
        </div>
        <SheetTitle className="text-xl">Terms of Service & Privacy Policy</SheetTitle>
        <SheetDescription>
          Please read and accept the following before setting up your workspace.
        </SheetDescription>
      </SheetHeader>

      <ScrollArea className="flex-1 min-h-0 onboarding-scroll-area">
        <div className="px-6 py-5 space-y-4">
          <div className="rounded-lg border bg-muted/30 text-sm text-muted-foreground leading-relaxed overflow-y-auto max-h-72 p-4 space-y-3">
            <p className="font-semibold text-foreground">Clarik Terms of Service</p>
            <p>By using Clarik, you agree to these terms. Clarik provides a software platform for entertainment booking management. Your use of the platform is subject to the following conditions.</p>
            <p><span className="font-medium text-foreground">Account Responsibility.</span> You are responsible for all activity that occurs under your account. Keep your credentials secure and notify us immediately of any unauthorized use.</p>
            <p><span className="font-medium text-foreground">Data & Privacy.</span> We collect and process data as described in our Privacy Policy. You retain ownership of your data. We process it solely to provide the service. We do not sell your data to third parties.</p>
            <p><span className="font-medium text-foreground">Acceptable Use.</span> You agree not to use Clarik for any unlawful purpose, to send spam or unsolicited messages in violation of applicable law, or to interfere with the platform's operation.</p>
            <p><span className="font-medium text-foreground">Email & SMS Compliance.</span> You are responsible for complying with applicable laws (CAN-SPAM, TCPA, GDPR, etc.) when using Clarik's communication tools. Cold outreach must include an unsubscribe mechanism and must only target recipients who may lawfully receive such messages.</p>
            <p><span className="font-medium text-foreground">Payments.</span> Subscription fees are billed per your plan. You authorize us to charge your payment method on file. Refunds are subject to our refund policy.</p>
            <p><span className="font-medium text-foreground">Limitation of Liability.</span> Clarik is provided "as is." To the maximum extent permitted by law, we are not liable for indirect, incidental, or consequential damages arising from your use of the platform.</p>
            <p><span className="font-medium text-foreground">Modifications.</span> We may update these terms from time to time. Continued use of Clarik after notice of changes constitutes acceptance of the updated terms.</p>
            <p className="font-semibold text-foreground mt-2">Privacy Policy Summary</p>
            <p>We collect information you provide (name, email, billing info), usage data, and event/booking data you enter. We use this to operate, improve, and support the platform. We share data only with service providers needed to deliver the service (e.g. payment processors, cloud infrastructure). You may request deletion of your data at any time by contacting support.</p>
            <p className="text-xs">Last updated: June 2026. Full legal versions available at clarik.io/terms and clarik.io/privacy.</p>
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-border p-3">
            <Checkbox
              id="tos-accept"
              checked={accepted}
              onCheckedChange={(c) => setAccepted(!!c)}
              data-testid="checkbox-tos-accept"
            />
            <label htmlFor="tos-accept" className="text-sm leading-snug cursor-pointer">
              I have read and agree to Clarik's{" "}
              <a href="https://clarik.io/terms" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="https://clarik.io/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                Privacy Policy
              </a>
              .
            </label>
          </div>
        </div>
      </ScrollArea>

      <div className="px-6 py-4 border-t shrink-0 flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={submitting || !accepted}
          data-testid="button-tos-submit"
        >
          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Accept &amp; Continue
        </Button>
      </div>
    </OnboardingSheet>
  );
}

// ── Drawer 1: Workspace Identity (org name + business type) ───────────
function WorkspaceIdentityDrawer({
  open,
  stepLabel,
  onSubmit,
  onDismiss,
  aiProps,
}: {
  open: boolean;
  stepLabel: string;
  onSubmit: () => void;
  onDismiss: () => void;
  aiProps: OnboardingAIProps;
}) {
  const { toast } = useToast();
  const { buildPath, workspaceSlug } = useWorkspace();
  const [orgName, setOrgName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data: branding } = useQuery<{ workspaceName: string | null }>({
    queryKey: ["/api/branding/public", workspaceSlug],
    enabled: open,
  });
  const { data: types } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ["/api/platform/business-types"],
    enabled: open,
  });
  const { data: bizData } = useQuery<{ businessType: string | null }>({
    queryKey: [buildPath("/api/workspace/business-type")],
    enabled: open,
  });

  useEffect(() => {
    if (!branding) return;
    const current = branding.workspaceName?.trim() || "";
    if (current && current !== "Warehouse Productions") {
      setOrgName(current);
    }
  }, [branding]);

  useEffect(() => {
    if (bizData?.businessType) setBusinessType(bizData.businessType);
  }, [bizData]);

  const handleSubmit = async () => {
    const trimmedName = orgName.trim();
    if (!trimmedName) {
      toast({ title: "Organization name required", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest("PATCH", buildPath("/api/workspace/name"), { name: trimmedName });
      if (businessType.trim()) {
        await apiRequest("PATCH", buildPath("/api/workspace/business-type"), { businessType: businessType.trim() });
        queryClient.invalidateQueries({ queryKey: [buildPath("/api/workspace/business-type")] });
      }
      await queryClient.refetchQueries({ queryKey: ["/api/branding/public", workspaceSlug] });
      onSubmit();
    } catch (err: any) {
      toast({ title: "Error", description: err?.message || "Could not save workspace info.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingSheet open={open} onDismiss={onDismiss}>
      <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
        <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
          <span>{stepLabel}</span>
          <CreditIndicator creditsUsed={aiProps.creditsUsed} creditCap={aiProps.creditCap} />
        </div>
        <SheetTitle className="text-xl">Set up your workspace</SheetTitle>
        <SheetDescription>
          Name your organization and tell us what type of entertainment business you run.
        </SheetDescription>
      </SheetHeader>

      <ScrollArea className="flex-1 min-h-0 onboarding-scroll-area">
        <div className="px-6 py-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="org-name">
              Organization name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="org-name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
              placeholder="e.g. Acme Entertainment Group"
              maxLength={120}
              autoFocus
              data-testid="input-org-name"
            />
            <p className="text-xs text-muted-foreground">
              This is how clients and collaborators will see your business. Shown top-left and on outgoing emails.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="business-type">
              Business type <span className="text-xs text-muted-foreground">(optional — helps Clarik AI tailor suggestions)</span>
            </Label>
            <Select value={businessType} onValueChange={setBusinessType}>
              <SelectTrigger id="business-type" data-testid="select-business-type">
                <SelectValue placeholder="Select your business type…" />
              </SelectTrigger>
              <SelectContent>
                {(types ?? []).map((t) => (
                  <SelectItem key={t.id} value={t.name} data-testid={`option-business-type-${t.id}`}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              e.g. Magician, Comedian, DJ, Speaker, Musician, Event Planner. Can only be changed by a Clarik admin later.
            </p>
          </div>

          <OnboardingAIPanel {...aiProps} />
        </div>
      </ScrollArea>

      <div className="px-6 py-4 border-t shrink-0 flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={submitting || !orgName.trim()}
          data-testid="button-workspace-identity-submit"
        >
          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Continue
        </Button>
      </div>
    </OnboardingSheet>
  );
}

// ── Drawer 2: Connections (skippable) ─────────────────────────────────
function ConnectionsDrawer({
  open,
  stepLabel,
  onSubmit,
  onDismiss,
}: {
  open: boolean;
  stepLabel: string;
  onSubmit: () => void;
  onDismiss: () => void;
}) {
  const { buildPath } = useWorkspace();

  const connections = [
    {
      icon: <Mail className="w-5 h-5 text-red-500" />,
      name: "Gmail",
      description: "Send confirmation emails, drip campaigns, and follow-ups directly from your connected Gmail inbox.",
      settingsPath: buildPath("/settings/email-settings"),
      testId: "link-connect-gmail",
    },
    {
      icon: <Calendar className="w-5 h-5 text-blue-500" />,
      name: "Google Calendar",
      description: "Sync bookings to Google Calendar so your talent's availability stays current and double-bookings are prevented.",
      settingsPath: buildPath("/settings/calendar"),
      testId: "link-connect-calendar",
    },
    {
      icon: <CreditCard className="w-5 h-5 text-indigo-500" />,
      name: "Stripe",
      description: "Accept deposits and balance payments online. Stripe Connect lets clients pay directly from their invoice link.",
      settingsPath: buildPath("/settings/billing"),
      testId: "link-connect-stripe",
    },
    {
      icon: <CreditCard className="w-5 h-5 text-blue-400" />,
      name: "PayPal",
      description: "Offer PayPal as an alternative payment method on your client-facing invoice portal.",
      settingsPath: buildPath("/settings/billing"),
      testId: "link-connect-paypal",
    },
  ];

  return (
    <OnboardingSheet open={open} onDismiss={onDismiss}>
      <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
        <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
          <span>{stepLabel}</span>
          <Badge variant="outline" className="text-[10px] normal-case tracking-normal">Optional</Badge>
        </div>
        <SheetTitle className="text-xl">Connect your tools</SheetTitle>
        <SheetDescription>
          These integrations unlock email sending, calendar sync, and online payments. You can connect them now or any time from Settings.
        </SheetDescription>
      </SheetHeader>

      <ScrollArea className="flex-1 min-h-0 onboarding-scroll-area">
        <div className="px-6 py-5 space-y-3">
          {connections.map((c) => (
            <div
              key={c.name}
              className="flex items-start gap-3 rounded-lg border border-border p-4"
              data-testid={`card-connection-${c.name.toLowerCase()}`}
            >
              <div className="mt-0.5 shrink-0">{c.icon}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{c.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{c.description}</p>
              </div>
              <Link href={c.settingsPath}>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1.5"
                  data-testid={c.testId}
                >
                  Set up
                  <ExternalLink className="w-3 h-3" />
                </Button>
              </Link>
            </div>
          ))}

          <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-muted-foreground">
            <Info className="w-3.5 h-3.5 inline mr-1.5 text-primary" />
            You can always connect these later from <span className="font-medium text-foreground">Admin → Settings</span>. Skip this step to continue setup.
          </div>
        </div>
      </ScrollArea>

      <div className="px-6 py-4 border-t shrink-0 flex justify-end gap-2">
        <Button variant="outline" onClick={onSubmit} data-testid="button-connections-skip">
          Skip for now
        </Button>
        <Button onClick={onSubmit} data-testid="button-connections-submit">
          Continue
        </Button>
      </div>
    </OnboardingSheet>
  );
}

// ── Talent form shape ──────────────────────────────────────────────────
interface TalentForm {
  id: string;
  name: string;
  email: string;
  bio: string;
  photoUrl: string;
  websiteUrl: string;
  eventDescription: string;
  googleReviewUrl: string;
}

function blankTalent(): TalentForm {
  return {
    id: Math.random().toString(36).slice(2),
    name: "",
    email: "",
    bio: "",
    photoUrl: "",
    websiteUrl: "",
    eventDescription: "",
    googleReviewUrl: "",
  };
}

// ── Drawer 3: Talent ───────────────────────────────────────────────────
function TalentDrawer({
  open,
  stepLabel,
  onSubmit,
  onDismiss,
  aiProps,
}: {
  open: boolean;
  stepLabel: string;
  onSubmit: () => void;
  onDismiss: () => void;
  aiProps: OnboardingAIProps;
}) {
  const { toast } = useToast();
  const { buildPath } = useWorkspace();
  const [isTalent, setIsTalent] = useState<boolean | null>(null);
  const [forms, setForms] = useState<TalentForm[]>([blankTalent()]);
  const [submitting, setSubmitting] = useState(false);

  const { data: existingTalent = [] } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
    enabled: open,
  });

  const updateForm = (id: string, field: keyof TalentForm, value: string) => {
    setForms((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [field]: value } : f))
    );
  };

  const removeForm = (id: string) => {
    if (forms.length === 1) return;
    setForms((prev) => prev.filter((f) => f.id !== id));
  };

  const addForm = () => setForms((prev) => [...prev, blankTalent()]);

  const handleSubmit = async () => {
    const valid = forms.filter((f) => f.name.trim());
    if (valid.length === 0 && existingTalent.length === 0) {
      toast({ title: "Name required", description: "Please enter at least one talent name.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      for (let i = 0; i < valid.length; i++) {
        const f = valid[i];
        await apiRequest("POST", buildPath("/api/talent"), {
          name: f.name.trim(),
          email: f.email.trim() || undefined,
          bio: f.bio.trim() || undefined,
          photoUrl: f.photoUrl.trim() || undefined,
          websiteUrl: f.websiteUrl.trim() || undefined,
          eventDescription: f.eventDescription.trim() || undefined,
          googleReviewUrl: f.googleReviewUrl.trim() || undefined,
          isActive: true,
          isDefault: existingTalent.length === 0 && i === 0,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/talent"] });
      onSubmit();
    } catch (err: any) {
      toast({ title: "Error", description: err?.message || "Failed to create talent.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingSheet open={open} onDismiss={onDismiss}>
      <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
        <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
          <span>{stepLabel}</span>
          <CreditIndicator creditsUsed={aiProps.creditsUsed} creditCap={aiProps.creditCap} />
        </div>
        <SheetTitle className="text-xl">Your talent profile</SheetTitle>
        <SheetDescription>
          Add the performer(s) you book. You can always add more later from Admin → Talent.
        </SheetDescription>
      </SheetHeader>

      <ScrollArea className="flex-1 min-h-0 onboarding-scroll-area">
        <div className="px-6 py-5 space-y-6">

          {/* Are you talent? */}
          <div className="space-y-3">
            <p className="text-sm font-medium">Are you a performer or entertainer yourself?</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setIsTalent(true)}
                className={`flex-1 rounded-lg border-2 py-3 px-4 text-sm font-medium transition-colors ${
                  isTalent === true
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border hover:border-primary/50"
                }`}
                data-testid="button-is-talent-yes"
              >
                Yes, I'm the talent
              </button>
              <button
                type="button"
                onClick={() => setIsTalent(false)}
                className={`flex-1 rounded-lg border-2 py-3 px-4 text-sm font-medium transition-colors ${
                  isTalent === false
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border hover:border-primary/50"
                }`}
                data-testid="button-is-talent-no"
              >
                No, I manage talent
              </button>
            </div>
          </div>

          {/* Existing talent (if any seeded) */}
          {existingTalent.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">Already in your workspace:</p>
              <div className="space-y-1">
                {existingTalent.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2">
                    <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium">{t.name}</span>
                    {t.isDefault && <Badge variant="secondary" className="text-[10px]"><Star className="w-2.5 h-2.5 mr-1" />Default</Badge>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Talent forms */}
          {forms.map((form, idx) => (
            <Card key={form.id} className="border-border">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-muted-foreground">
                    {isTalent === true && idx === 0
                      ? "Your profile"
                      : existingTalent.length === 0 && idx === 0
                      ? "Talent profile"
                      : `Additional talent #${idx + 1}`}
                  </p>
                  {forms.length > 1 && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeForm(form.id)}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor={`talent-name-${form.id}`}>
                      Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id={`talent-name-${form.id}`}
                      value={form.name}
                      onChange={(e) => updateForm(form.id, "name", e.target.value)}
                      placeholder={isTalent === true && idx === 0 ? "Your stage name or full name" : "e.g. The Acme Duo"}
                      data-testid={`input-talent-name-${idx}`}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`talent-email-${form.id}`}>Email</Label>
                    <Input
                      id={`talent-email-${form.id}`}
                      type="email"
                      value={form.email}
                      onChange={(e) => updateForm(form.id, "email", e.target.value)}
                      placeholder="talent@example.com"
                      data-testid={`input-talent-email-${idx}`}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`talent-website-${form.id}`}>Website URL</Label>
                    <Input
                      id={`talent-website-${form.id}`}
                      value={form.websiteUrl}
                      onChange={(e) => updateForm(form.id, "websiteUrl", e.target.value)}
                      placeholder="https://"
                      data-testid={`input-talent-website-${idx}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor={`talent-photo-${form.id}`}>Photo URL</Label>
                    <Input
                      id={`talent-photo-${form.id}`}
                      value={form.photoUrl}
                      onChange={(e) => updateForm(form.id, "photoUrl", e.target.value)}
                      placeholder="https://..."
                      data-testid={`input-talent-photo-${idx}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor={`talent-review-${form.id}`}>
                      Google Review Link
                      <span className="text-xs text-muted-foreground ml-1">(optional — used in post-event emails via {"{{google_review_url}}"})</span>
                    </Label>
                    <div className="relative">
                      <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                      <Input
                        id={`talent-review-${form.id}`}
                        value={form.googleReviewUrl}
                        onChange={(e) => updateForm(form.id, "googleReviewUrl", e.target.value)}
                        placeholder="https://g.page/r/..."
                        className="pl-8"
                        data-testid={`input-talent-google-review-${idx}`}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Paste your Google Business review URL so clients can easily leave a review after their event.
                    </p>
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor={`talent-bio-${form.id}`}>Bio</Label>
                    <VoiceToTextTextarea
                      id={`talent-bio-${form.id}`}
                      value={form.bio}
                      onChange={(e) => updateForm(form.id, "bio", e.target.value)}
                      placeholder="A short public bio…"
                      rows={3}
                      data-testid={`input-talent-bio-${idx}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor={`talent-desc-${form.id}`}>Event description</Label>
                    <VoiceToTextTextarea
                      id={`talent-desc-${form.id}`}
                      value={form.eventDescription}
                      onChange={(e) => updateForm(form.id, "eventDescription", e.target.value)}
                      placeholder="Describe the experience clients can expect…"
                      rows={2}
                      data-testid={`input-talent-event-desc-${idx}`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Add another */}
          <button
            type="button"
            onClick={addForm}
            className="flex items-center gap-2 text-sm text-primary hover:underline"
            data-testid="button-add-another-talent"
          >
            <Plus className="w-3.5 h-3.5" />
            Add another talent profile
          </button>
          <p className="text-xs text-muted-foreground -mt-3">
            You can always add more from Admin → Talent later.
          </p>

          <OnboardingAIPanel {...aiProps} />
        </div>
      </ScrollArea>

      <div className="px-6 py-4 border-t shrink-0 flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="button-talent-submit"
        >
          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Continue
        </Button>
      </div>
    </OnboardingSheet>
  );
}

// ── Drawer 4: Event Offerings ──────────────────────────────────────────
interface OfferingForm {
  service: string;
  name: string;
  rate: string;
  minutesBefore: number;
  minutesAfter: number;
  isDefault: boolean;
  noSetRate: boolean;
  talentId: string;
}

function blankOffering(talentId: string): OfferingForm {
  return {
    service: "Entertainment Performance",
    name: "",
    rate: "0",
    minutesBefore: 30,
    minutesAfter: 60,
    isDefault: false,
    noSetRate: false,
    talentId,
  };
}

function formatDuration(minutes: number) {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function OfferingFormFields({
  value,
  onChange,
  onSave,
  onCancel,
  saving,
  saveLabel,
  talentList,
}: {
  value: OfferingForm;
  onChange: (f: OfferingForm) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  saveLabel: string;
  talentList: Talent[];
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label>Service <span className="text-xs text-muted-foreground">(shown on invoices)</span></Label>
          <Input
            value={value.service}
            onChange={(e) => onChange({ ...value, service: e.target.value })}
            placeholder="e.g. Entertainment Performance"
            data-testid="input-offering-service"
          />
        </div>
        <div>
          <Label>Description <span className="text-destructive">*</span></Label>
          <Input
            value={value.name}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
            placeholder="e.g. 60 Minute Show"
            data-testid="input-offering-name"
          />
        </div>
        <div>
          <Label>Rate ($)</Label>
          {value.noSetRate ? (
            <div className="h-9 flex items-center px-3 text-sm text-muted-foreground bg-muted/50 rounded-md border">
              — entered per booking
            </div>
          ) : (
            <Input
              type="number"
              min={0}
              step="0.01"
              value={value.rate}
              onChange={(e) => onChange({ ...value, rate: e.target.value })}
              placeholder="0.00"
              data-testid="input-offering-rate"
            />
          )}
        </div>
        <div>
          <Label>Time before event (min) <span className="text-xs text-muted-foreground">setup/arrival</span></Label>
          <Input
            type="number"
            min={0}
            value={value.minutesBefore}
            onChange={(e) => onChange({ ...value, minutesBefore: parseInt(e.target.value) || 0 })}
            data-testid="input-offering-before"
          />
        </div>
        <div>
          <Label>Time after event (min) <span className="text-xs text-muted-foreground">teardown/travel</span></Label>
          <Input
            type="number"
            min={0}
            value={value.minutesAfter}
            onChange={(e) => onChange({ ...value, minutesAfter: parseInt(e.target.value) || 0 })}
            data-testid="input-offering-after"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 p-3 bg-muted/40 rounded-md text-sm">
        <Clock className="w-3.5 h-3.5 text-muted-foreground" />
        Total calendar block: <strong>{formatDuration(value.minutesBefore + value.minutesAfter)}</strong>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id="offering-nosetrate"
            checked={value.noSetRate}
            onCheckedChange={(c) => onChange({ ...value, noSetRate: !!c, rate: !!c ? "0" : value.rate })}
            data-testid="checkbox-offering-nosetrate"
          />
          <label htmlFor="offering-nosetrate" className="text-sm cursor-pointer">
            No set rate — enter price per booking
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="offering-default"
            checked={value.isDefault}
            onCheckedChange={(c) => onChange({ ...value, isDefault: !!c })}
            data-testid="checkbox-offering-default"
          />
          <label htmlFor="offering-default" className="text-sm cursor-pointer">
            <Star className="w-3 h-3 inline mr-1 text-amber-500" />
            Set as default offering
          </label>
        </div>
      </div>

      {talentList.length > 1 && (
        <div>
          <Label>Talent</Label>
          <select
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={value.talentId}
            onChange={(e) => onChange({ ...value, talentId: e.target.value })}
            data-testid="select-offering-talent"
          >
            {talentList.map((t) => (
              <option key={t.id} value={t.id}>{t.name}{t.isDefault ? " (Default)" : ""}</option>
            ))}
          </select>
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="button-offering-cancel">
          Cancel
        </Button>
        <Button size="sm" onClick={onSave} disabled={saving} data-testid="button-offering-save">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
          {saveLabel}
        </Button>
      </div>
    </div>
  );
}

function OfferingsDrawer({
  open,
  stepLabel,
  onSubmit,
  onDismiss,
  aiProps,
}: {
  open: boolean;
  stepLabel: string;
  onSubmit: () => void;
  onDismiss: () => void;
  aiProps: OnboardingAIProps;
}) {
  const { toast } = useToast();
  const { buildPath } = useWorkspace();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: talent = [] } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
    enabled: open,
  });
  const { data: offerings = [], isLoading } = useQuery<EventOffering[]>({
    queryKey: ["/api/event-offerings"],
    enabled: open,
    refetchInterval: open ? 2000 : false,
  });

  const defaultTalentId = talent[0]?.id || "";
  const [form, setForm] = useState<OfferingForm>(() => blankOffering(""));
  const [editForm, setEditForm] = useState<OfferingForm>(() => blankOffering(""));

  useEffect(() => {
    if (talent.length > 0 && !form.talentId) {
      setForm((prev) => ({ ...prev, talentId: talent[0].id }));
    }
  }, [talent]);

  // Auto-seed one default custom-pricing offering on first open if the workspace has none.
  const seededDefaultsRef = useRef(false);
  useEffect(() => {
    if (seededDefaultsRef.current) return;
    if (!open || isLoading || offerings.length > 0 || !defaultTalentId) return;
    seededDefaultsRef.current = true;
    (async () => {
      try {
        await apiRequest("POST", buildPath("/api/event-offerings"), {
          service: "Entertainment Performance",
          name: "Custom Event",
          rate: "0",
          minutesBefore: 30,
          minutesAfter: 60,
          isDefault: true,
          noSetRate: true,
          talentId: defaultTalentId,
          sortOrder: 0,
        });
      } catch {
        // Swallow — user can manually add if seeding fails.
      }
      queryClient.invalidateQueries({ queryKey: ["/api/event-offerings"] });
    })();
  }, [open, isLoading, offerings.length, defaultTalentId]);

  const startEdit = (o: EventOffering) => {
    setEditForm({
      service: o.service || "Entertainment Performance",
      name: o.name,
      rate: o.rate || "0",
      minutesBefore: o.minutesBefore,
      minutesAfter: o.minutesAfter,
      isDefault: o.isDefault,
      noSetRate: (o as any).noSetRate || false,
      talentId: o.talentId || defaultTalentId,
    });
    setEditingId(o.id);
  };

  const createMutation = useMutation({
    mutationFn: async (data: OfferingForm) => {
      const res = await apiRequest("POST", buildPath("/api/event-offerings"), {
        ...data,
        sortOrder: offerings.length,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/event-offerings"] });
      toast({ title: "Offering added" });
      setForm(blankOffering(defaultTalentId));
      setIsAdding(false);
    },
    onError: (e: any) => toast({ title: "Error", description: e?.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: OfferingForm }) => {
      const res = await apiRequest("PATCH", buildPath(`/api/event-offerings/${id}`), data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/event-offerings"] });
      toast({ title: "Offering updated" });
      setEditingId(null);
    },
    onError: (e: any) => toast({ title: "Error", description: e?.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", buildPath(`/api/event-offerings/${id}`)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/event-offerings"] });
      toast({ title: "Offering removed" });
    },
    onError: (e: any) => toast({ title: "Error", description: e?.message, variant: "destructive" }),
  });

  const handleSaveNew = () => {
    if (!form.name.trim()) {
      toast({ title: "Description required", variant: "destructive" });
      return;
    }
    createMutation.mutate(form);
  };

  return (
    <OnboardingSheet open={open} onDismiss={onDismiss}>
      <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
        <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
          <span>{stepLabel}</span>
          <CreditIndicator creditsUsed={aiProps.creditsUsed} creditCap={aiProps.creditCap} />
        </div>
        <SheetTitle className="text-xl">Event offerings</SheetTitle>
        <SheetDescription>
          Add the types of events or services you sell — e.g. "60 Minute Event", "Holiday Special". You can always edit these later and set dynamic pricing for different client types.
        </SheetDescription>
      </SheetHeader>

      <ScrollArea className="flex-1 min-h-0 onboarding-scroll-area">
        <div className="px-6 py-5 space-y-4">
          {isLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Existing offerings */}
          {offerings.map((o) => {
            const isEdit = editingId === o.id;
            return (
              <Card key={o.id} className={isEdit ? "ring-2 ring-primary" : ""}>
                <CardContent className="p-4">
                  {isEdit ? (
                    <OfferingFormFields
                      value={editForm}
                      onChange={setEditForm}
                      onSave={() => updateMutation.mutate({ id: o.id, data: editForm })}
                      onCancel={() => setEditingId(null)}
                      saving={updateMutation.isPending}
                      saveLabel="Save changes"
                      talentList={talent}
                    />
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{o.name}</span>
                          {o.isDefault && (
                            <Badge variant="secondary" className="text-[10px]">
                              <Star className="w-2.5 h-2.5 mr-1 text-amber-500" />Default
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-[10px]">
                            {(o as any).noSetRate ? "Custom Rate" : `$${parseFloat(o.rate || "0").toFixed(2)}`}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Invoice service: {o.service || "Entertainment Performance"}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatDuration(o.minutesBefore)} before · {formatDuration(o.minutesAfter)} after
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(o)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => deleteMutation.mutate(o.id)}
                          disabled={deleteMutation.isPending}
                          data-testid={`button-delete-offering-${o.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Add new form */}
          {isAdding ? (
            <Card className="ring-2 ring-primary">
              <CardContent className="p-4">
                <p className="text-sm font-medium mb-4">New offering</p>
                <OfferingFormFields
                  value={form}
                  onChange={setForm}
                  onSave={handleSaveNew}
                  onCancel={() => { setIsAdding(false); setForm(blankOffering(defaultTalentId)); }}
                  saving={createMutation.isPending}
                  saveLabel="Add offering"
                  talentList={talent}
                />
              </CardContent>
            </Card>
          ) : (
            <button
              type="button"
              onClick={() => { setForm(blankOffering(defaultTalentId)); setIsAdding(true); }}
              className="flex items-center gap-2 text-sm text-primary hover:underline"
              data-testid="button-add-offering"
            >
              <Plus className="w-3.5 h-3.5" />
              Add{offerings.length > 0 ? " another" : " an"} offering
            </button>
          )}

          <p className="text-xs text-muted-foreground">
            You can always edit these later and set dynamic pricing for different client types from Admin → Event Offerings.
          </p>

          <OnboardingAIPanel {...aiProps} />
        </div>
      </ScrollArea>

      <div className="px-6 py-4 border-t shrink-0 flex justify-end">
        <Button onClick={onSubmit} data-testid="button-offerings-submit">
          Continue
        </Button>
      </div>
    </OnboardingSheet>
  );
}

// ── Drawer 5: Late Fees (skippable) ────────────────────────────────────
function LateFeesDrawer({
  open,
  stepLabel,
  onSubmit,
  onDismiss,
}: {
  open: boolean;
  stepLabel: string;
  onSubmit: () => void;
  onDismiss: () => void;
}) {
  const [lateFeesOpen, setLateFeesOpen] = useState(false);

  return (
    <OnboardingSheet open={open} onDismiss={onDismiss}>
      <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
        <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
          <span>{stepLabel}</span>
          <Badge variant="outline" className="text-[10px] normal-case tracking-normal">Optional</Badge>
        </div>
        <SheetTitle className="text-xl">Late fee policies</SheetTitle>
        <SheetDescription>
          Configure automatic late fees to encourage on-time payments. You can always set this up later from Settings.
        </SheetDescription>
      </SheetHeader>

      <ScrollArea className="flex-1 min-h-0 onboarding-scroll-area">
        <div className="px-6 py-5 space-y-4">
          <div className="space-y-3">
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Protect your revenue with late fees</p>
                  <p className="text-sm text-muted-foreground leading-snug">
                    Late fees are automatically added to overdue invoices based on rules you define — such as a flat fee or percentage that kicks in after a grace period. You can configure different rules for each client type.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border p-4 space-y-2">
              <p className="text-sm font-medium">Features include:</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> Flat or percentage-based late fees</li>
                <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> Configurable grace period before fees apply</li>
                <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> Per-state legal exceptions and caps</li>
                <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> Different rules for each client type</li>
              </ul>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={() => setLateFeesOpen(true)}
            data-testid="button-configure-late-fees"
          >
            Configure Late Fees
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            You can also configure this later from <span className="font-medium">Settings → Late Fees</span>.
          </p>
        </div>
      </ScrollArea>

      <div className="px-6 py-4 border-t shrink-0 flex justify-end gap-2">
        <Button variant="outline" onClick={onSubmit} data-testid="button-late-fees-skip">
          Skip for now
        </Button>
        <Button onClick={onSubmit} data-testid="button-late-fees-submit">
          Continue
        </Button>
      </div>

      <LateFeeSettingsDialog
        open={lateFeesOpen}
        onOpenChange={setLateFeesOpen}
      />
    </OnboardingSheet>
  );
}

// ── Drawer 6: Fallback Confirmation Email ──────────────────────────────
function ConfirmationEmailDrawer({
  open,
  stepLabel,
  onSubmit,
  onDismiss,
}: {
  open: boolean;
  stepLabel: string;
  onSubmit: () => void;
  onDismiss: () => void;
}) {
  const { toast } = useToast();
  const { buildPath } = useWorkspace();
  const [subject, setSubject] = useState(DEFAULT_FALLBACK_TEMPLATE_SUBJECT);
  const [body, setBody] = useState(DEFAULT_FALLBACK_TEMPLATE_BODY);
  const [saving, setSaving] = useState(false);

  const { data: allTemplates = [], isLoading } = useQuery<ConfirmationEmailTemplate[]>({
    queryKey: [buildPath("/api/confirmation-templates")],
    enabled: open,
  });

  const existingFallback = useMemo(
    () => allTemplates.find((t) => (t as any).isWorkspaceFallback),
    [allTemplates]
  );

  useEffect(() => {
    if (existingFallback) {
      setSubject(existingFallback.subject);
      setBody(existingFallback.body);
    }
  }, [existingFallback]);

  const handleSave = async () => {
    if (!subject.trim() || !body.trim()) {
      toast({ title: "Subject and body required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await apiRequest("POST", buildPath("/api/confirmation-templates"), {
        clientType: "_workspace_fallback",
        subject: subject.trim(),
        body: body.trim(),
        isWorkspaceFallback: true,
        isActive: true,
        variantType: "standard",
      });
      queryClient.invalidateQueries({ queryKey: [buildPath("/api/confirmation-templates")] });
      toast({ title: "Confirmation template saved" });
      onSubmit();
    } catch (err: any) {
      toast({ title: "Error", description: err?.message || "Could not save template.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingSheet open={open} onDismiss={onDismiss}>
      <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
        <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
          <span>{stepLabel}</span>
        </div>
        <SheetTitle className="text-xl">Default confirmation email</SheetTitle>
        <SheetDescription>
          This template is sent to clients when no specific template matches their type. Edit the subject and body, then save or continue with the default.
        </SheetDescription>
      </SheetHeader>

      <ScrollArea className="flex-1 min-h-0 onboarding-scroll-area">
        <div className="px-6 py-5 space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-xs text-muted-foreground">
                <Sparkles className="w-3.5 h-3.5 inline mr-1.5 text-primary" />
                Supports merge variables like <code className="font-mono">{"{{contact_name}}"}</code>, <code className="font-mono">{"{{event_date}}"}</code>, <code className="font-mono">{"{{invoice_url}}"}</code>, <code className="font-mono">{"{{google_review_url}}"}</code>. Customise per client type later from <span className="font-medium text-foreground">Communications → Confirmations</span>.
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-subject">Subject line</Label>
                <Input
                  id="confirm-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Booking Confirmation — {{account_name}}"
                  data-testid="input-confirmation-subject"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-body">Email body</Label>
                <Textarea
                  id="confirm-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={14}
                  className="font-mono text-xs resize-none"
                  data-testid="textarea-confirmation-body"
                />
              </div>
            </>
          )}
        </div>
      </ScrollArea>

      <div className="px-6 py-4 border-t shrink-0 flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={handleSave}
          disabled={saving || isLoading}
          data-testid="button-confirmation-use-default"
        >
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Use default &amp; continue
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || isLoading}
          data-testid="button-confirmation-save"
        >
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Save &amp; continue
        </Button>
      </div>
    </OnboardingSheet>
  );
}

// ── Drawer 7: Client Types ─────────────────────────────────────────────
interface LocalClientType {
  id: string;
  name: string;
  isNew: boolean;
  isDeleted: boolean;
  originalName: string;
}

function ClientTypesDrawer({
  open,
  stepLabel,
  onBack,
  onSubmit,
  onDismiss,
  aiProps,
}: {
  open: boolean;
  stepLabel: string;
  onBack: () => void;
  onSubmit: () => void;
  onDismiss: () => void;
  aiProps: OnboardingAIProps;
}) {
  const { toast } = useToast();
  const { buildPath } = useWorkspace();
  const { data: serverTypes = [], isLoading } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
    enabled: open,
  });

  const [localTypes, setLocalTypes] = useState<LocalClientType[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [newName, setNewName] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const seededRef = useRef(false);

  useEffect(() => {
    if (seededRef.current || isLoading) return;
    seededRef.current = true;
    if (serverTypes.length > 0) {
      setLocalTypes(
        serverTypes.map((t) => ({
          id: t.id,
          name: t.name,
          isNew: false,
          isDeleted: false,
          originalName: t.name,
        }))
      );
    } else {
      setLocalTypes(
        DEFAULT_CLIENT_TYPES.map((name) => ({
          id: `new-${Math.random().toString(36).slice(2)}`,
          name,
          isNew: true,
          isDeleted: false,
          originalName: name,
        }))
      );
    }
  }, [serverTypes, isLoading]);

  const visibleTypes = localTypes.filter((t) => !t.isDeleted);

  const startEdit = (t: LocalClientType) => {
    setEditingId(t.id);
    setEditValue(t.name);
  };

  const saveEdit = (id: string) => {
    if (!editValue.trim()) return;
    setLocalTypes((prev) =>
      prev.map((t) => (t.id === id ? { ...t, name: editValue.trim() } : t))
    );
    setEditingId(null);
    setEditValue("");
  };

  const deleteType = (id: string) => {
    setLocalTypes((prev) =>
      prev.map((t) => (t.id === id ? { ...t, isDeleted: true } : t))
    );
  };

  const addNew = () => {
    if (!newName.trim()) return;
    setLocalTypes((prev) => [
      ...prev,
      {
        id: `new-${Math.random().toString(36).slice(2)}`,
        name: newName.trim(),
        isNew: true,
        isDeleted: false,
        originalName: newName.trim(),
      },
    ]);
    setNewName("");
    setShowAdd(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      for (const t of localTypes) {
        if (t.isDeleted && !t.isNew) {
          await apiRequest("DELETE", buildPath(`/api/client-types/${t.id}`));
        } else if (t.isNew && !t.isDeleted) {
          await apiRequest("POST", buildPath("/api/client-types"), { name: t.name });
        } else if (!t.isNew && !t.isDeleted && t.name !== t.originalName) {
          await apiRequest("PATCH", buildPath(`/api/client-types/${t.id}`), { name: t.name });
        }
      }
      queryClient.invalidateQueries({ queryKey: ["/api/client-types"] });
      onSubmit();
    } catch (err: any) {
      toast({ title: "Error saving client types", description: err?.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingSheet open={open} onDismiss={onDismiss}>
      <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
        <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
          <span>{stepLabel}</span>
          <CreditIndicator creditsUsed={aiProps.creditsUsed} creditCap={aiProps.creditCap} />
        </div>
        <SheetTitle className="text-xl">Client types</SheetTitle>
        <SheetDescription>
          These categories describe who books you. Edit the defaults to match your market, or add your own.
        </SheetDescription>
      </SheetHeader>

      <ScrollArea className="flex-1 min-h-0 onboarding-scroll-area">
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground">Client Types describe who books you</span> — corporate events, weddings, private parties, and so on. Together with Regions, they are the main filters for your lead and client pipeline, so you can quickly slice your book of business any way you need.
          </p>

          <div className="space-y-2">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {visibleTypes.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center gap-2 rounded-lg border border-border px-3 py-2.5"
                  data-testid={`client-type-row-${t.id}`}
                >
                  {editingId === t.id ? (
                    <>
                      <Input
                        className="h-7 text-sm flex-1"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit(t.id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        autoFocus
                        data-testid={`input-client-type-edit-${t.id}`}
                      />
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => saveEdit(t.id)}>
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm">{t.name}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(t)}>
                        <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => deleteType(t.id)}
                        disabled={visibleTypes.length <= 1}
                        data-testid={`button-delete-client-type-${t.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              ))}

              {showAdd ? (
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    className="h-8 text-sm flex-1"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addNew();
                      if (e.key === "Escape") { setShowAdd(false); setNewName(""); }
                    }}
                    placeholder="New client type…"
                    autoFocus
                    data-testid="input-new-client-type"
                  />
                  <Button size="sm" variant="outline" onClick={addNew} disabled={!newName.trim()} data-testid="button-confirm-add-client-type">
                    Add
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowAdd(true)}
                  className="flex items-center gap-2 text-sm text-primary hover:underline pt-1"
                  data-testid="button-add-client-type"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add new
                </button>
              )}
            </>
          )}
          </div>

          <OnboardingAIPanel {...aiProps} />
        </div>
      </ScrollArea>

      <div className="px-6 py-4 border-t shrink-0 flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} data-testid="button-client-types-back">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Button onClick={handleSubmit} disabled={submitting || isLoading} data-testid="button-client-types-submit">
          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Continue
        </Button>
      </div>
    </OnboardingSheet>
  );
}

// ── Drawer 8: Regions ──────────────────────────────────────────────────
function RegionsDrawer({
  open,
  stepLabel,
  onSubmit,
  onDismiss,
  isPending,
  aiProps,
}: {
  open: boolean;
  stepLabel: string;
  onSubmit: () => void;
  onDismiss: () => void;
  isPending: boolean;
  aiProps: OnboardingAIProps;
}) {
  const { toast } = useToast();
  const { buildPath } = useWorkspace();
  const [newRegionName, setNewRegionName] = useState("");

  const regionsQueryKey = [buildPath("/api/regions")];

  const { data: regions = [], isLoading } = useQuery<Region[]>({
    queryKey: regionsQueryKey,
    enabled: open,
  });

  const addMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", buildPath("/api/regions"), { name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: regionsQueryKey });
      setNewRegionName("");
    },
    onError: (e: any) => toast({ title: "Error", description: e?.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", buildPath(`/api/regions/${id}`)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: regionsQueryKey }),
    onError: (e: any) => toast({ title: "Error", description: e?.message, variant: "destructive" }),
  });

  const addRegion = () => {
    if (!newRegionName.trim()) return;
    addMutation.mutate(newRegionName.trim());
  };

  return (
    <OnboardingSheet open={open} onDismiss={onDismiss}>
      <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
        <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
          <span>{stepLabel}</span>
          <CreditIndicator creditsUsed={aiProps.creditsUsed} creditCap={aiProps.creditCap} />
        </div>
        <SheetTitle className="text-xl">Regions</SheetTitle>
        <SheetDescription>
          Regions are geographic areas you assign to leads and clients — they let you filter your pipeline and report by territory. Even a single broad region like "Midwest" or "Texas" is enough to start.
        </SheetDescription>
      </SheetHeader>

      <ScrollArea className="flex-1 min-h-0 onboarding-scroll-area">
        <div className="px-6 py-5 space-y-5">
          <p className="text-sm text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground">Regions and Client Types are your sources of truth</span> for sorting through leads and clients. Region is where your leads and clients are physically located — we highly recommend <span className="font-medium text-foreground">City, State</span> as the region format, but this is fully customizable and can be changed later.
          </p>

          <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-muted-foreground">
            <Sparkles className="w-3.5 h-3.5 inline mr-1.5 text-primary" />
            Not sure how to structure your regions? Click <span className="font-medium text-foreground">Ask Clarik AI</span> below — it can suggest a setup based on where you perform.
          </div>

          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {regions.length > 0 && (
                <div className="space-y-2">
                  {regions.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center gap-2 rounded-lg border border-border px-3 py-2.5"
                      data-testid={`region-row-${r.id}`}
                    >
                      <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="flex-1 text-sm">{r.name}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => deleteMutation.mutate(r.id)}
                        disabled={deleteMutation.isPending}
                        data-testid={`button-delete-region-${r.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  value={newRegionName}
                  onChange={(e) => setNewRegionName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addRegion(); }}
                  placeholder="e.g. Midwest, Texas, Northeast…"
                  data-testid="input-new-region"
                />
                <Button
                  variant="outline"
                  onClick={addRegion}
                  disabled={!newRegionName.trim() || addMutation.isPending}
                  data-testid="button-add-region"
                >
                  {addMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </>
          )}

          <OnboardingAIPanel {...aiProps} />

        </div>
      </ScrollArea>

      <div className="px-6 py-4 border-t shrink-0 flex justify-end">
        <Button
          onClick={onSubmit}
          disabled={isPending || regions.length === 0}
          data-testid="button-regions-submit"
        >
          {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {regions.length === 0 ? "Add at least one region" : "Finish setup"}
        </Button>
      </div>
    </OnboardingSheet>
  );
}

// ── Main component ─────────────────────────────────────────────────────
export function OnboardingWizard({
  profile,
  canManageTalent,
  canManageClientTypes,
  canManageRegions,
  canManageEventOfferings,
  canManageBrand,
}: OnboardingWizardProps) {
  const { toast } = useToast();
  const { buildPath } = useWorkspace();
  const [activeDrawer, setActiveDrawer] = useState<DrawerStep | null>(null);
  const [confirmDismissOpen, setConfirmDismissOpen] = useState(false);
  const [wizardCreditsUsed, setWizardCreditsUsed] = useState(0);
  const [wizardCreditWarning, setWizardCreditWarning] = useState(false);
  const wizardSessionId = useMemo(() => Math.random().toString(36).slice(2) + Date.now(), []);

  const { data: onboardingConfig } = useQuery<{ creditsPerMessage: number; creditGift: number }>({
    queryKey: [buildPath("/api/voice-assistant/onboarding-config")],
  });
  const creditCap = onboardingConfig?.creditGift ?? 250;
  const creditsPerMessage = onboardingConfig?.creditsPerMessage ?? 3;

  // 9 steps total (0–8). Step label is 1-based.
  const totalSteps = 9;
  const stepLabel = (n: number) => `Step ${n} of ${totalSteps}`;

  const makeAIProps = (stepKey: OnboardingStepKey): OnboardingAIProps => ({
    stepKey,
    creditsUsed: wizardCreditsUsed,
    creditCap,
    creditsPerMessage,
    sessionId: wizardSessionId,
    onCreditsConsumed: setWizardCreditsUsed,
    onCreditWarning: () => setWizardCreditWarning(true),
  });

  // Auto-open the first drawer (ToS) after a short delay so the app renders behind it.
  useEffect(() => {
    const t = setTimeout(() => setActiveDrawer(0), 150);
    return () => clearTimeout(t);
  }, []);

  const completeOnboarding = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", buildPath("/api/profile/onboarding"), { completed: true });
      return (await res.json()) as UserProfile;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/profile"], data);
      queryClient.setQueryData(["/api/profile-real"], data);
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/profile-real"] });
      toast({
        title: "You're all set!",
        description: "Workspace setup complete. Welcome to Clarik!",
      });
    },
    onError: () => {
      toast({ title: "Couldn't finish setup", description: "Please try again.", variant: "destructive" });
    },
  });

  // Non-admin: friendly welcome card (no setup permissions needed)
  const hasNoSetupPermissions =
    !canManageTalent &&
    !canManageClientTypes &&
    !canManageRegions &&
    !canManageEventOfferings;

  if (hasNoSetupPermissions) {
    return (
      <div
        className="fixed inset-0 z-50 bg-background overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-welcome-title"
        data-testid="dialog-onboarding-welcome"
      >
        <div className="min-h-full flex items-start justify-center py-10 px-4 sm:py-16">
          <div className="w-full max-w-2xl space-y-6">
            <header className="space-y-2">
              <h1
                id="onboarding-welcome-title"
                className="flex items-center gap-2 text-2xl sm:text-3xl font-semibold"
              >
                <Sparkles className="w-6 h-6 text-primary" />
                Welcome aboard!
              </h1>
              <p className="text-muted-foreground">
                Here's a quick tour of the three things you'll use most. Your
                workspace setup is already handled by your admin — you can jump
                straight in.
              </p>
            </header>

            <ul className="space-y-3">
              <li className="rounded-lg border border-border p-4 flex items-start gap-3">
                <UserPlus className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Leads</div>
                  <p className="text-sm text-muted-foreground mt-1 leading-snug">
                    People you haven't booked yet. Add prospects, log calls and emails, and mark them Booked when an event is confirmed.
                  </p>
                </div>
              </li>
              <li className="rounded-lg border border-border p-4 flex items-start gap-3">
                <Users className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Clients</div>
                  <p className="text-sm text-muted-foreground mt-1 leading-snug">
                    Venues and contacts you've booked at least once. They show up here automatically when you log an event from a lead.
                  </p>
                </div>
              </li>
              <li className="rounded-lg border border-border p-4 flex items-start gap-3">
                <CalendarPlus className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Log an Event</div>
                  <p className="text-sm text-muted-foreground mt-1 leading-snug">
                    Use this when a booking is confirmed. It creates the client if new, books the date, and generates an invoice in one step.
                  </p>
                </div>
              </li>
            </ul>

            <div className="rounded-lg border border-border bg-muted/40 p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground leading-snug">
                Talent, regions, and other workspace settings are managed by your admin. If something looks missing, ask them to finish the workspace setup.
              </p>
            </div>

            <div className="flex items-center justify-end pt-2">
              <Button
                onClick={() => completeOnboarding.mutate()}
                disabled={completeOnboarding.isPending}
                data-testid="button-onboarding-welcome-dismiss"
              >
                {completeOnboarding.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Got it, let's go
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const requestDismiss = () => setConfirmDismissOpen(true);

  return (
    <>
      {/* Step 0: Terms of Service */}
      <TermsDrawer
        open={activeDrawer === 0}
        stepLabel={stepLabel(1)}
        onSubmit={() => setActiveDrawer(1)}
        onDismiss={requestDismiss}
      />

      {/* Step 1: Workspace Identity (org name + business type) */}
      <WorkspaceIdentityDrawer
        open={activeDrawer === 1}
        stepLabel={stepLabel(2)}
        onSubmit={() => setActiveDrawer(2)}
        onDismiss={requestDismiss}
        aiProps={makeAIProps("workspace_identity")}
      />

      {/* Step 2: Connections (optional) */}
      <ConnectionsDrawer
        open={activeDrawer === 2}
        stepLabel={stepLabel(3)}
        onSubmit={() => setActiveDrawer(3)}
        onDismiss={requestDismiss}
      />

      {/* Step 3: Talent */}
      <TalentDrawer
        open={activeDrawer === 3}
        stepLabel={stepLabel(4)}
        onSubmit={() => setActiveDrawer(4)}
        onDismiss={requestDismiss}
        aiProps={makeAIProps("talent")}
      />

      {/* Step 4: Event Offerings */}
      <OfferingsDrawer
        open={activeDrawer === 4}
        stepLabel={stepLabel(5)}
        onSubmit={() => setActiveDrawer(5)}
        onDismiss={requestDismiss}
        aiProps={makeAIProps("offerings")}
      />

      {/* Step 5: Late Fees (optional) */}
      <LateFeesDrawer
        open={activeDrawer === 5}
        stepLabel={stepLabel(6)}
        onSubmit={() => setActiveDrawer(6)}
        onDismiss={requestDismiss}
      />

      {/* Step 6: Fallback Confirmation Email */}
      <ConfirmationEmailDrawer
        open={activeDrawer === 6}
        stepLabel={stepLabel(7)}
        onSubmit={() => setActiveDrawer(7)}
        onDismiss={requestDismiss}
      />

      {/* Step 7: Client Types */}
      <ClientTypesDrawer
        open={activeDrawer === 7}
        stepLabel={stepLabel(8)}
        onBack={() => setActiveDrawer(6)}
        onSubmit={() => setActiveDrawer(8)}
        onDismiss={requestDismiss}
        aiProps={makeAIProps("client_types")}
      />

      {/* Step 8: Regions (completes onboarding) */}
      <RegionsDrawer
        open={activeDrawer === 8}
        stepLabel={stepLabel(9)}
        onSubmit={() => completeOnboarding.mutate()}
        onDismiss={requestDismiss}
        isPending={completeOnboarding.isPending}
        aiProps={makeAIProps("regions")}
      />

      {/* Credit-limit warning (shared across all steps) */}
      <AlertDialog open={wizardCreditWarning} onOpenChange={setWizardCreditWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>You've used your free onboarding credits</AlertDialogTitle>
            <AlertDialogDescription>
              You've used all {creditCap} free AI credits included with onboarding. You can still complete setup — just continue without the AI panel, or finish up quickly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setWizardCreditWarning(false)} data-testid="button-credit-warning-ok">
              Got it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDismissOpen} onOpenChange={setConfirmDismissOpen}>
        <AlertDialogContent data-testid="dialog-confirm-onboarding-dismiss">
          <AlertDialogHeader>
            <AlertDialogTitle>Skip setup?</AlertDialogTitle>
            <AlertDialogDescription>
              You can finish it later from your profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDismissOpen(false)}
              disabled={completeOnboarding.isPending}
              data-testid="button-cancel-onboarding-dismiss"
            >
              Keep going
            </Button>
            <Button
              onClick={() => {
                completeOnboarding.mutate(undefined, {
                  onSuccess: () => setConfirmDismissOpen(false),
                });
              }}
              disabled={completeOnboarding.isPending}
              data-testid="button-confirm-onboarding-dismiss"
            >
              {completeOnboarding.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Skip for now
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
